import type { ComponentType } from 'react';
import { BookmarkIcon, EyeIcon } from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { shortenCount } from '@rocco/utils/format-number';
import type { DetailItemStats } from './detail-stats';

interface DetailProfileStatsProps {
    stats: DetailItemStats;
    /** `light` = the trail's grey on a pale page; `dark` = its grey on #181818. */
    theme?: 'light' | 'dark';
    className?: string;
}

/**
 * The professional's profile views and saves, set in the breadcrumb trail's
 * own type on its line: an eye and a bookmark, each with its count. A count
 * of zero draws nothing, so a new profile shows no noughts.
 */
export const DetailProfileStats = ({
    stats,
    theme = 'light',
    className,
}: DetailProfileStatsProps) => {
    const items: {
        icon: ComponentType<{ className?: string }>;
        value: number;
        label: string;
    }[] = [];
    if (stats.views > 0) {
        items.push({
            icon: EyeIcon,
            value: stats.views,
            label: stats.views === 1 ? 'profile view' : 'profile views',
        });
    }
    if (stats.saves > 0) {
        items.push({
            icon: BookmarkIcon,
            value: stats.saves,
            label: stats.saves === 1 ? 'save' : 'saves',
        });
    }
    if (items.length === 0) return null;

    return (
        <ul
            className={cn(
                'm-0 flex list-none items-center gap-[16px] p-0 font-sans font-normal whitespace-nowrap max-md:text-[13px] md:text-[14px]',
                // A step quieter than the trail's mobile ink, like the
                // chevrons — the desktop pale grey vanished on the grey page.
                theme === 'dark'
                    ? 'text-[#767676]'
                    : 'text-[#b3b3b2] max-md:text-[#767676]',
                className,
            )}
        >
            {items.map(({ icon: Icon, value, label }) => (
                <li
                    key={label}
                    title={`${value.toLocaleString()} ${label}`}
                    className="inline-flex items-center gap-[6px] leading-[1.5]"
                >
                    <Icon className="size-[14px] shrink-0" aria-hidden />
                    {shortenCount(value)}
                    <span className="sr-only"> {label}</span>
                </li>
            ))}
        </ul>
    );
};
